/**
 * Helpers for /api/playbooks/status polling when a run returns multiple sequence_ids.
 */

function coerceSequenceId(value) {
  if (value == null || value === '') return '';
  if (typeof value === 'object') {
    return String(value.sequence_id ?? value.sequenceId ?? value.id ?? '').trim();
  }
  return String(value).trim();
}

function collectSequenceIds(payload) {
  if (!payload || typeof payload !== 'object') return [];

  const topIds = payload.sequence_ids;
  if (Array.isArray(topIds) && topIds.length > 0) {
    const out = topIds.map(coerceSequenceId).filter(Boolean);
    if (out.length > 0) return out;
  }

  const rows = payload.sequences;
  if (Array.isArray(rows) && rows.length > 0) {
    const fromRows = rows.map(coerceSequenceId).filter(Boolean);
    if (fromRows.length > 0) return fromRows;
  }

  const single = coerceSequenceId(
    payload.sequence_id ?? payload.sequenceId ?? payload.id
  );
  return single ? [single] : [];
}

export function normalizeRunSequenceIds(payload) {
  const fromTop = collectSequenceIds(payload);
  if (fromTop.length > 0) return fromTop;
  if (payload?.data && typeof payload.data === 'object') {
    return collectSequenceIds(payload.data);
  }
  return [];
}

function unwrapStatusPayload(data) {
  if (!data || typeof data !== 'object') return data;
  if (data.progress || Array.isArray(data.steps)) return data;
  const inner = data.data || data.result || data.payload;
  if (inner && typeof inner === 'object' && (inner.progress || inner.status != null || Array.isArray(inner.steps))) {
    return inner;
  }
  if (data.status != null) return data;
  if (inner && typeof inner === 'object') return inner;
  return data;
}

function normalizeSequenceStatus(status) {
  const s = String(status || '').toLowerCase();
  if (['complete', 'completed', 'done', 'ready', 'success'].includes(s)) return 'complete';
  if (['failed', 'error', 'failure'].includes(s)) return 'failed';
  if (['partial_failure', 'partial-failure'].includes(s)) return 'partial_failure';
  if (['no_steps', 'empty'].includes(s)) return 'no_steps';
  // [2026-10-05] 'interrupted' = generation stopped early and nothing will pick
  // it up (the backend saw queue-state rows with no tenant-wide activity for
  // 10 minutes — almost always a deploy that drained the instance running the
  // background generation). It is TERMINAL: without it here the poller treats
  // the unknown status as "still going" and spins forever, which is exactly the
  // silent hang this was added to end.
  if (['interrupted', 'orphaned'].includes(s)) return 'interrupted';
  return s;
}

/** Terminal DIY sequence statuses. Branch only on these — not progress.stuck. */
export const DIY_TERMINAL_STATUSES = new Set(['complete', 'failed', 'no_steps', 'partial_failure', 'interrupted']);

/** Floor for the optional safety cap: expected_seconds × 3, at least 5 minutes. */
export const DIY_GENERATION_MIN_CAP_MS = 5 * 60 * 1000;

export function diyGenerationSafetyCapMs(expectedSeconds) {
  const s = Number(expectedSeconds);
  const fromHint = Number.isFinite(s) && s > 0 ? s * 3 * 1000 : DIY_GENERATION_MIN_CAP_MS;
  return Math.max(DIY_GENERATION_MIN_CAP_MS, fromHint);
}

export function describeDiyGenerationTimeout() {
  return 'Still generating, check the Outbox shortly';
}

const STEP_COMPLETE_STATUSES = new Set([
  'draft_ready',
  'complete',
  'completed',
  'ready',
  'generated',
  'done',
]);

export function parsePlaybookStatusData(data) {
  const empty = {
    status: '',
    complete: 0,
    total: 0,
    generating: 0,
    stuck: 0,
    queued: 0,
    failed: 0,
    blocked: 0,
    blocked_steps: [],
  };
  const root = unwrapStatusPayload(data);
  if (!root || typeof root !== 'object') return empty;

  const status = normalizeSequenceStatus(root.status);
  const progress = root.progress && typeof root.progress === 'object' ? root.progress : {};
  let complete = Number(
    progress.complete ??
      progress.completed ??
      progress.ready ??
      root.generated ??
      root.completed ??
      root.complete ??
      root.ready ??
      0
  );
  let total = Number(
    progress.total ??
      progress.total_steps ??
      root.total ??
      root.total_steps ??
      root.steps_total ??
      0
  );
  let generating = Number(progress.generating ?? root.generating) || 0;
  const stuck = Number(progress.stuck ?? root.stuck) || 0;
  const queued = Number(progress.queued ?? root.queued) || 0;
  const failed = Number(progress.failed ?? root.failed) || 0;
  const blocked = Number(progress.blocked ?? root.blocked) || 0;
  const blocked_steps = Array.isArray(root.blocked_steps) ? root.blocked_steps : [];

  const steps = Array.isArray(root.steps) ? root.steps : [];
  if (steps.length > 0) {
    if (!Number.isFinite(total) || total <= 0) total = steps.length;
    if (!Number.isFinite(complete) || complete < 0) complete = 0;
    if (complete === 0) {
      const fromSteps = steps.filter((step) =>
        STEP_COMPLETE_STATUSES.has(String(step?.status || '').toLowerCase())
      ).length;
      if (fromSteps > 0) complete = fromSteps;
    }
    if (!generating) {
      generating = steps.filter((step) =>
        ['generating', 'pending', 'in_progress', 'running'].includes(
          String(step?.status || '').toLowerCase()
        )
      ).length;
    }
  }

  return {
    status,
    complete: Number.isFinite(complete) ? complete : 0,
    total: Number.isFinite(total) ? total : 0,
    generating,
    stuck,
    queued,
    failed,
    blocked,
    blocked_steps,
    // [2026-10-05] Carried through so the poller can show the backend's own
    // explanation of an interrupted run rather than inventing one. Undefined on
    // every normal response — this parser drops unknown fields, so without these
    // two lines the message the backend sends would never reach the UI.
    user_message: root.user_message ?? undefined,
    retry_after_seconds: root.retry_after_seconds ?? undefined,
  };
}

/**
 * Per-sequence polling phase. `partial_failure` alone does not finish while work remains.
 * Event/standard playbook preview still uses the accounted>=total heuristic.
 * DIY run polling uses {@link diyGenerationPollDecision} and requires `status: complete`.
 * @returns {'pending'|'done'|'failed'|'no_steps'}
 */
export function sequencePollPhase(parsed) {
  if (parsed.status === 'failed') return 'failed';
  if (parsed.status === 'no_steps') return 'no_steps';
  if (parsed.status === 'complete') return 'done';
  const { complete, total, generating, stuck, failed, blocked } = parsed;
  const accounted = complete + failed + blocked;
  if (Number.isFinite(total) && total > 0 && generating === 0 && stuck === 0 && accounted >= total) {
    return 'done';
  }
  return 'pending';
}

export function allSequencesStatusComplete(parsedList) {
  const list = Array.isArray(parsedList) ? parsedList : [];
  return list.length > 0 && list.every((p) => String(p?.status || '').toLowerCase() === 'complete');
}

/**
 * True while this DIY sequence is not terminal. Branch only on `status`.
 * `progress.queued` / `stuck` / `stuck_diagnostic` are never a stop condition.
 * Empty/unknown payloads stay in-flight so a failed first poll does not look finished.
 */
export function isSequenceStillGenerating(parsed) {
  if (!parsed || typeof parsed !== 'object') return true;
  const status = String(parsed.status || '').toLowerCase();
  if (!status) return true;
  return !DIY_TERMINAL_STATUSES.has(status);
}

function collectBlockedSteps(parsedList) {
  return (Array.isArray(parsedList) ? parsedList : []).flatMap((p) => (
    Array.isArray(p?.blocked_steps) ? p.blocked_steps : []
  ));
}

/**
 * DIY run poller. Done only when every sequence reports a terminal status.
 * Never treat progress.stuck, queued, or stuck_diagnostic as failure.
 */
export function diyGenerationPollDecision(parsedList) {
  const list = Array.isArray(parsedList) ? parsedList : [];
  const agg = aggregateParsedList(list);
  if (!list.length) return { action: 'wait', agg };

  if (list.some(isSequenceStillGenerating)) {
    return { action: 'wait', agg };
  }

  const failedSeq = list.filter((p) => String(p?.status || '').toLowerCase() === 'failed').length;
  const noStepsSeq = list.filter((p) => String(p?.status || '').toLowerCase() === 'no_steps').length;
  const partialSeq = list.filter((p) => String(p?.status || '').toLowerCase() === 'partial_failure').length;
  const interruptedList = list.filter((p) => String(p?.status || '').toLowerCase() === 'interrupted');
  const blockedSteps = collectBlockedSteps(list);

  if (failedSeq > 0) {
    return { action: 'failed', agg, failedSeq, blockedSteps };
  }
  if (list.every((p) => String(p?.status || '').toLowerCase() === 'no_steps')) {
    return { action: 'no_steps', agg, noStepsSeq };
  }
  // [2026-10-05] Interrupted is checked BEFORE partial_failure: it is a
  // kinder and more accurate explanation of the same end state, and it carries
  // the backend's own user-facing wording plus a retry delay.
  if (interruptedList.length > 0) {
    const withMessage = interruptedList.find((p) => p?.user_message) || interruptedList[0];
    return {
      action: 'interrupted',
      agg,
      blockedSteps,
      interruptedSeq: interruptedList.length,
      userMessage: withMessage?.user_message || null,
      retryAfterSeconds: Number(withMessage?.retry_after_seconds) || 300,
    };
  }
  if (partialSeq > 0) {
    return { action: 'partial_failure', agg, blockedSteps };
  }
  return { action: 'complete', agg };
}

export function aggregateParsedList(parsedList) {
  return parsedList.reduce(
    (acc, p) => ({
      complete: acc.complete + (Number.isFinite(p.complete) ? p.complete : 0),
      total: acc.total + (Number.isFinite(p.total) ? p.total : 0),
      generating: acc.generating + p.generating,
      stuck: acc.stuck + p.stuck,
      queued: acc.queued + (Number(p.queued) || 0),
      failed: acc.failed + p.failed,
      blocked: acc.blocked + p.blocked,
    }),
    { complete: 0, total: 0, generating: 0, stuck: 0, queued: 0, failed: 0, blocked: 0 }
  );
}

export function playbookStatusRequestUrl(apiBaseUrl, sequenceId, tenantId, userId) {
  return `${apiBaseUrl}/api/playbooks/status/${encodeURIComponent(sequenceId)}?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId || '')}`;
}

function abortSignalTimeout(ms) {
  if (typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
    return AbortSignal.timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

export function playbookStatusFetchOptions(tenantId, userId) {
  const opts = {
    credentials: 'include',
    signal: abortSignalTimeout(15000),
  };
  const headers = {};
  const tid = String(tenantId ?? '').trim();
  const uid = String(userId ?? '').trim();
  if (tid) headers['X-Tenant-ID'] = tid;
  if (uid) headers['X-User-ID'] = uid;
  if (Object.keys(headers).length) opts.headers = headers;
  return opts;
}

/**
 * Parallel status fetches with Promise.allSettled. On reject/non-OK, reuses last good body
 * from lastBodyById (mutated on each success), keyed by sequence id string.
 */
export async function fetchPlaybookStatusBodiesAllSettled(
  apiBaseUrl,
  sequenceIds,
  tenantId,
  userId,
  lastBodyById
) {
  const ids = (Array.isArray(sequenceIds) ? sequenceIds : [])
    .map((id) => String(id).trim())
    .filter(Boolean);
  const cache = lastBodyById && typeof lastBodyById === 'object' ? lastBodyById : {};

  const settled = await Promise.allSettled(
    ids.map(async (sid) => {
      const url = playbookStatusRequestUrl(apiBaseUrl, sid, tenantId, userId);
      const res = await fetch(url, playbookStatusFetchOptions(tenantId, userId));
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json().catch(() => ({}));
      if (data && typeof data === 'object') {
        cache[sid] = data;
      }
      return data;
    })
  );

  const bodies = ids.map((sid, i) => {
    const r = settled[i];
    if (r.status === 'fulfilled' && r.value && typeof r.value === 'object') {
      return r.value;
    }
    const prev = cache[sid];
    return prev && typeof prev === 'object' ? prev : {};
  });
  bodies.failedCount = settled.filter((r) => r.status === 'rejected').length;
  bodies.failedStatuses = settled
    .filter((r) => r.status === 'rejected')
    .map((r) => String(r.reason?.message || r.reason || '').trim())
    .filter(Boolean);
  return bodies;
}

/** Consecutive ticks where every sequence poll returned non-OK. Reset on any success. */
export function nextConsecutivePollFailures(previousStreak, failedCount, sequenceCount) {
  const n = Number(sequenceCount) || 0;
  const failed = Number(failedCount) || 0;
  if (n > 0 && failed >= n) return (Number(previousStreak) || 0) + 1;
  return 0;
}

export function describeStatusPollHttpError(failedStatuses) {
  const codes = (Array.isArray(failedStatuses) ? failedStatuses : [])
    .map((s) => String(s).trim())
    .filter(Boolean);
  const unique = [...new Set(codes)];
  const statusBit = unique.length ? ` (HTTP ${unique.join(', ')})` : '';
  return `Could not read generation status${statusBit}. Check Outbox — drafts may already be ready.`;
}

export function formatGeneratingStartedLine(expectedSeconds) {
  const safeExpected =
    Number.isFinite(Number(expectedSeconds)) && Number(expectedSeconds) > 0
      ? Math.round(Number(expectedSeconds))
      : 300;
  return `Generating messages — about ${safeExpected}s`;
}

export function formatProgressIssueParts(aggregate, { includeStuck = false } = {}) {
  const parts = [];
  const failed = Number(aggregate?.failed) || 0;
  const blocked = Number(aggregate?.blocked) || 0;
  const stuck = Number(aggregate?.stuck) || 0;
  if (failed > 0) parts.push(`${failed} failed`);
  if (blocked > 0) parts.push(`${blocked} blocked`);
  if (includeStuck && stuck > 0) parts.push(`${stuck} stuck`);
  return parts;
}

export function describeTerminalProgress(aggregate, blockedSteps) {
  const steps = Array.isArray(blockedSteps) ? blockedSteps : [];
  const blockedFromSteps = steps.length;
  const merged = {
    ...aggregate,
    blocked: blockedFromSteps || Number(aggregate?.blocked) || 0,
  };
  const parts = formatProgressIssueParts(merged, { includeStuck: true });
  if (!parts.length) {
    return 'Open Outbox to review.';
  }
  return `${parts.join(', ')}. Open Outbox to review.`;
}

/**
 * Progress copy. `expectedSeconds` is a hint only — never a stop condition.
 * Counts come from summed `progress.complete` / `progress.total`. Do not use
 * `steps_created` from the run response as a fallback denominator.
 */
export function formatGeneratingProgressLine(aggregate, expectedSeconds, fallbackTotal) {
  const fromProgress = Number(aggregate?.total) || 0;
  const fromHint = Math.max(0, Math.floor(Number(fallbackTotal) || 0));
  const total = fromProgress > 0 ? fromProgress : fromHint;
  const complete = Math.min(Math.max(0, Number(aggregate?.complete) || 0), total || Infinity);
  const issues = formatProgressIssueParts(aggregate);
  const suffix = issues.length ? ` · ${issues.join(', ')}` : '';
  if (total <= 0) {
    return formatGeneratingStartedLine(expectedSeconds);
  }
  return `Generating ${Number.isFinite(complete) ? complete : 0} of ${total} messages${suffix}`;
}

export function formatDraftsReadyLine(aggregate, fallbackTotal) {
  const complete = Number(aggregate?.complete) || 0;
  const n = complete > 0 ? complete : Math.max(0, Math.floor(Number(fallbackTotal) || 0));
  if (n > 0) return `${n} drafts ready`;
  return 'Drafts ready';
}

export function describeSequencesFailed(sequenceCount, failedSequenceCount, messagesGenerated) {
  const g = Number(messagesGenerated) || 0;
  return `${failedSequenceCount} of ${sequenceCount} sequence${sequenceCount === 1 ? '' : 's'} failed (${g} message${g === 1 ? '' : 's'} generated)`;
}
